import React, { useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area } from "recharts";

const INDUSTRIES = [
  { value: "consulting", label: "Consulting / Professional Services", expenseRatio: 0.18, topExpenses: ["Software", "Marketing", "Travel", "Meals", "Office Supplies"] },
  { value: "ecommerce", label: "E-Commerce / Retail", expenseRatio: 0.45, topExpenses: ["COGS", "Shipping", "Marketing", "Software", "Storage"] },
  { value: "real_estate", label: "Real Estate", expenseRatio: 0.30, topExpenses: ["Maintenance", "Marketing", "Insurance", "Utilities", "Office"] },
  { value: "construction", label: "Construction / Trades", expenseRatio: 0.55, topExpenses: ["Materials", "Equipment", "Insurance", "Vehicle", "Permits"] },
  { value: "healthcare", label: "Healthcare / Medical", expenseRatio: 0.35, topExpenses: ["Supplies", "Insurance", "Rent", "Software", "Compliance"] },
  { value: "technology", label: "Technology / SaaS", expenseRatio: 0.22, topExpenses: ["Cloud Hosting", "Software", "Marketing", "Payroll", "Legal"] },
  { value: "food_bev", label: "Food & Beverage", expenseRatio: 0.60, topExpenses: ["COGS", "Rent", "Utilities", "Payroll", "Equipment"] },
  { value: "creative", label: "Creative / Design Agency", expenseRatio: 0.20, topExpenses: ["Software", "Marketing", "Contractors", "Equipment", "Travel"] },
  { value: "fitness", label: "Fitness / Wellness", expenseRatio: 0.38, topExpenses: ["Rent", "Equipment", "Insurance", "Marketing", "Supplies"] },
  { value: "legal", label: "Legal Services", expenseRatio: 0.25, topExpenses: ["Rent", "Research Tools", "Insurance", "Marketing", "Continuing Education"] },
  { value: "other", label: "Other", expenseRatio: 0.30, topExpenses: ["Rent", "Marketing", "Software", "Supplies", "Utilities"] },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct"];
const DONUT_COLORS = ["#1e3a5f", "#2d5a8e", "#3d7abd", "#6b9fd4", "#99c0e4", "#c2daf0", "#e0ecf8", "#f0f5fb"];
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const fmtFull = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function generateDemoData(businessName, industry, monthlyRevenue) {
  const ind = INDUSTRIES.find(i => i.value === industry) || INDUSTRIES[INDUSTRIES.length - 1];
  const expRatio = ind.expenseRatio;
  const monthlyExpenses = monthlyRevenue * expRatio;
  const monthlyNet = monthlyRevenue - monthlyExpenses;

  // Generate 10 months of cash flow with realistic variance
  const seed = businessName.length + monthlyRevenue;
  const seededRandom = (i) => {
    const x = Math.sin(seed + i * 7.3) * 10000;
    return x - Math.floor(x);
  };
  const cashflow = MONTHS.map((label, i) => {
    const variance = 0.85 + seededRandom(i) * 0.3;
    const rev = Math.round(monthlyRevenue * variance);
    const expVariance = 0.8 + seededRandom(i + 50) * 0.4;
    const exp = Math.round(monthlyExpenses * expVariance);
    return { label, revenue: rev, expenses: exp, net: rev - exp };
  });

  // YTD totals
  const ytdIncome = cashflow.reduce((s, m) => s + m.revenue, 0);
  const ytdExpenses = cashflow.reduce((s, m) => s + m.expenses, 0);
  const ytdNet = ytdIncome - ytdExpenses;
  const txnCount = Math.round(ytdIncome / (monthlyRevenue * 0.12)) + Math.round(ytdExpenses / 200);

  // Expense categories based on industry
  const expenseWeights = [0.30, 0.22, 0.16, 0.12, 0.10, 0.05, 0.03, 0.02];
  const expenseCategories = ind.topExpenses.map((cat, i) => ({
    category: cat,
    amount: Math.round(ytdExpenses * (expenseWeights[i] || 0.03))
  }));
  // Fill remaining
  const covered = expenseCategories.reduce((s, e) => s + e.amount, 0);
  if (covered < ytdExpenses) {
    expenseCategories.push({ category: "Other", amount: ytdExpenses - covered });
  }

  // Allocation data (Profit First percentages)
  const allocations = [
    { name: "Profit", value: Math.round(ytdIncome * 0.10), color: "#059669", percent: 10 },
    { name: "Tax", value: Math.round(ytdIncome * 0.15), color: "#f59e0b", percent: 15 },
    { name: "Owner Pay", value: Math.round(ytdIncome * 0.50), color: "#8b5cf6", percent: 50 },
    { name: "OpEx", value: Math.round(ytdIncome * 0.25), color: "#ef4444", percent: 25 },
  ];

  // Health score
  const profitMargin = Math.round((ytdNet / ytdIncome) * 100);
  const runway = monthlyExpenses > 0 ? Math.round((ytdNet / monthlyExpenses) * 10) / 10 : 0;
  const healthScore = Math.min(100, Math.max(20, Math.round(50 + profitMargin * 1.5 + Math.min(runway, 6) * 3)));

  // Smart accounts
  const smartAccounts = [
    { type: "Income Account", bank: `${businessName} Checking`, mask: String(1000 + Math.round(seededRandom(100) * 8999)), balance: Math.round(monthlyRevenue * 0.95), percent: 0, color: "#3b82f6", icon: "I" },
    { type: "Profit Account", bank: `${businessName} Profit`, mask: String(1000 + Math.round(seededRandom(101) * 8999)), balance: Math.round(ytdIncome * 0.10 * 0.6), percent: 10, color: "#10b981", icon: "P" },
    { type: "Owner's Pay", bank: `${businessName} Owner Pay`, mask: String(1000 + Math.round(seededRandom(102) * 8999)), balance: Math.round(monthlyRevenue * 0.50 * 0.8), percent: 50, color: "#8b5cf6", icon: "O" },
    { type: "Tax Account", bank: `${businessName} Tax Reserve`, mask: String(1000 + Math.round(seededRandom(103) * 8999)), balance: Math.round(ytdIncome * 0.15 * 0.7), percent: 15, color: "#f59e0b", icon: "T" },
    { type: "Operating Expenses", bank: `${businessName} OpEx`, mask: String(1000 + Math.round(seededRandom(104) * 8999)), balance: Math.round(monthlyExpenses * 1.5), percent: 25, color: "#ef4444", icon: "E" },
  ];

  const totalBalance = smartAccounts.reduce((s, a) => s + a.balance, 0);

  // Generate transactions
  const vendors = {
    income: [`Client A`, `Client B`, `Recurring Contract`, `Project Payment`, `${businessName} Sales`],
    expense: ind.topExpenses,
  };

  const transactions = [];
  for (let i = 0; i < 10; i++) {
    const isIncome = i < 4;
    const day = 20 - i * 2;
    const amount = isIncome
      ? Math.round(monthlyRevenue * (0.15 + seededRandom(i + 200) * 0.35))
      : -Math.round(monthlyExpenses * (0.05 + seededRandom(i + 300) * 0.2));
    transactions.push({
      date: `2026-04-${String(day).padStart(2, '0')}`,
      desc: isIncome ? vendors.income[i % vendors.income.length] : `${vendors.expense[i - 4] || 'Operating'} Payment`,
      amount,
      type: isIncome ? "income" : "expense",
      cat: isIncome ? "Income / Revenue" : (vendors.expense[(i - 4) % vendors.expense.length] || "Operating"),
      vendor: isIncome ? vendors.income[i % vendors.income.length] : "Vendor",
      recurring: i % 3 === 0,
    });
  }

  // Latest allocation breakdown
  const latestAllocation = allocations.map(a => ({
    name: smartAccounts.find(sa => sa.type.toLowerCase().includes(a.name.toLowerCase().split(" ")[0]))?.bank || a.name,
    mask: smartAccounts.find(sa => sa.type.toLowerCase().includes(a.name.toLowerCase().split(" ")[0]))?.mask || "0000",
    amount: Math.round(monthlyRevenue * (a.percent / 100)),
    pct: a.percent,
    done: a.name !== "OpEx",
  }));

  return {
    businessName,
    industry: ind,
    cashflow,
    ytdIncome, ytdExpenses, ytdNet, txnCount,
    expenseCategories,
    allocations,
    healthScore,
    profitMargin,
    runway,
    taxSaved: allocations.find(a => a.name === "Tax").value,
    reserve: Math.round(totalBalance * 0.42),
    smartAccounts,
    totalBalance,
    transactions,
    latestAllocation,
    monthlyRevenue,
  };
}

const DEMO_TABS = [
  { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { key: "accounts", label: "Smart Accounts", icon: "Wallet" },
  { key: "transactions", label: "Transactions", icon: "Receipt" },
  { key: "banking", label: "Bank Accounts", icon: "Building" },
  { key: "plans", label: "Compare Plans", icon: "Star" },
];

const GUIDE_STEPS = [
  { tab: "dashboard", title: "Your Financial Health at a Glance", description: "See your real-time business health score powered by AI — profit margins, cash runway, tax savings, and reserves — all calculated from your numbers." },
  { tab: "dashboard", title: "YTD Financial Summary", description: "Your income, expenses, and net profit — personalized from the revenue you entered. Always know where you stand." },
  { tab: "dashboard", title: "Cash Flow Trends", description: "Monthly income vs. expenses projected from your data. Spot trends, plan for seasonal dips, and make smarter decisions." },
  { tab: "accounts", title: "The Opturna Method", description: "Five dedicated accounts ensure every dollar has a job. Allocations happen on the 10th and 25th — your money splits automatically." },
  { tab: "transactions", title: "Smart Transaction Tracking", description: "All your business transactions in one place. Auto-categorized expenses and flagged deductions so you never miss a write-off." },
  { tab: "banking", title: "Connected Bank Accounts", description: "Link your bank accounts or add them manually. Track all your business account balances from a single view." },
  { tab: "plans", title: "Ready to Take Control?", description: "You've seen what Opturna can do with your numbers. Pick a plan, start your free trial, and take control of your business finances." },
];

// Tab content components — all use the `data` prop
const DemoDashboard = ({ data }) => (
  <div className="space-y-5">
    <Card className="overflow-hidden">
      <div className="h-2 bg-[#1e3a5f]" />
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wide">Financial Health</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-[#1e3a5f]">{data.healthScore}</span>
              <span className="text-stone-400">/100</span>
            </div>
            <Badge variant="outline" className="mt-1 text-[#1e3a5f]">
              {data.healthScore >= 80 ? "EXCELLENT" : data.healthScore >= 60 ? "GOOD" : data.healthScore >= 40 ? "FAIR" : "NEEDS WORK"}
            </Badge>
          </div>
          <div className="w-20 h-20">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={[{ value: data.healthScore }, { value: 100 - data.healthScore }]} innerRadius="60%" outerRadius="100%" startAngle={90} endAngle={-270} dataKey="value">
                  <Cell fill="#10b981" />
                  <Cell fill="#e5e7eb" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-[#1e3a5f]/5"><p className="text-xs text-[#1e3a5f] mb-1">Profit Margin</p><p className="text-lg font-bold text-[#1e3a5f]">{data.profitMargin}%</p></div>
          <div className="p-3 rounded-lg bg-blue-50"><p className="text-xs text-blue-600 mb-1">Runway</p><p className="text-lg font-bold text-blue-700">{data.runway} mo</p></div>
          <div className="p-3 rounded-lg bg-amber-50"><p className="text-xs text-amber-600 mb-1">Tax Saved</p><p className="text-lg font-bold text-amber-700">{fmt(data.taxSaved)}</p></div>
          <div className="p-3 rounded-lg bg-violet-50"><p className="text-xs text-violet-600 mb-1">Reserve</p><p className="text-lg font-bold text-violet-700">{fmt(data.reserve)}</p></div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">YTD Financial Summary</CardTitle>
          <Badge variant="outline">{data.txnCount} transactions</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-[#1e3a5f]/5"><p className="text-xs text-[#1e3a5f] mb-1">Income</p><p className="text-lg font-bold text-[#1e3a5f]">{fmt(data.ytdIncome)}</p></div>
          <div className="p-3 rounded-lg bg-red-50"><p className="text-xs text-red-600 mb-1">Expenses</p><p className="text-lg font-bold text-red-700">{fmt(data.ytdExpenses)}</p></div>
          <div className="p-3 rounded-lg bg-[#1e3a5f]/5"><p className="text-xs text-[#1e3a5f] mb-1">Net Profit</p><p className="text-lg font-bold text-[#1e3a5f]">{fmt(data.ytdNet)}</p></div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Monthly Cash Flow</CardTitle></CardHeader>
      <CardContent>
        <div className="h-48 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.cashflow} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={42} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }} formatter={(v) => [`$${Number(v).toLocaleString()}`, undefined]} />
              <Bar dataKey="revenue" name="Income" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#e57373" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-6 mt-2">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-[#1e3a5f]" /><span className="text-xs text-[#5a6577]">Income</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-[#e57373]" /><span className="text-xs text-[#5a6577]">Expenses</span></div>
        </div>
        <div className="mt-3 h-16 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.cashflow}>
              <defs><linearGradient id="demoNetGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1e3a5f" stopOpacity={0.3}/><stop offset="95%" stopColor="#1e3a5f" stopOpacity={0}/></linearGradient></defs>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }} formatter={(v) => [`$${Number(v).toLocaleString()}`, "Net Profit"]} />
              <Area type="monotone" dataKey="net" stroke="#1e3a5f" fill="url(#demoNetGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-center text-[10px] text-stone-400 mt-1">Net Profit Trend</p>
      </CardContent>
    </Card>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base text-[#1e3a5f]">Where Your Money Goes</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-36 h-36 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.expenseCategories} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="amount" nameKey="category" stroke="none">
                    {data.expenseCategories.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [fmt(v), undefined]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5">
              {data.expenseCategories.map((item, i) => (
                <div key={item.category} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="text-[#5a6577]">{item.category}</span>
                  </div>
                  <span className="font-medium text-[#1e3a5f]">{fmt(item.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Allocation Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-28 h-28 flex-shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.allocations} innerRadius="50%" outerRadius="100%" dataKey="value">
                    {data.allocations.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2">
              {data.allocations.map((item) => (
                <div key={item.name} className="flex items-center gap-2 p-2 rounded bg-stone-50">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <div className="min-w-0"><p className="text-xs text-stone-500 truncate">{item.name}</p><p className="font-medium text-sm">{fmt(item.value)}</p></div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const DemoAccounts = ({ data }) => (
  <div className="space-y-5">
    <Card className="bg-[#1e3a5f]/5 border-[#1e3a5f]/20">
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center">
            <svg className="w-5 h-5 text-[#1e3a5f]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          </div>
          <div>
            <p className="font-semibold text-[#1e3a5f]">The Opturna Method</p>
            <p className="text-sm text-[#5a6577]">Every dollar gets assigned a job. Allocate revenue on the <strong>10th</strong> and <strong>25th</strong> of each month.</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-stone-700">Account Setup</h3>
      <Badge className="bg-[#1e3a5f]">5/5 Configured</Badge>
    </div>
    <Progress value={100} className="h-2" />

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {data.smartAccounts.map((acc) => (
        <Card key={acc.type} className="border-t-4" style={{ borderTopColor: acc.color }}>
          <CardContent className="pt-4 pb-3 px-3">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: acc.color }}>{acc.icon}</div>
              <span className="text-xs text-stone-500">{acc.percent}%</span>
            </div>
            <p className="font-semibold text-sm text-stone-900 mb-0.5">{acc.type}</p>
            <p className="text-[11px] text-stone-500 truncate">{acc.bank}</p>
            <p className="text-[11px] text-stone-400 mb-2">**** {acc.mask}</p>
            <p className="text-lg font-bold text-[#1e3a5f]">{fmtFull(acc.balance)}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Latest Allocation</CardTitle>
          <Badge variant="outline">{fmt(data.monthlyRevenue)}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {data.latestAllocation.map((t) => (
            <div key={t.name} className={`flex items-center justify-between p-3 rounded-lg ${t.done ? 'bg-stone-50' : 'bg-amber-50/50'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${t.done ? 'bg-[#1e3a5f] border-[#1e3a5f]' : 'border-stone-300'}`}>
                  {t.done && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>}
                </div>
                <div>
                  <p className={`text-sm font-medium ${t.done ? 'line-through text-stone-400' : 'text-stone-900'}`}>{t.name}</p>
                  <p className="text-[11px] text-stone-400">**** {t.mask}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-[#1e3a5f]">{fmt(t.amount)}</p>
                <p className="text-[11px] text-stone-400">{t.pct}%</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

const DemoTransactions = ({ data }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-[#1e3a5f]">{fmt(data.ytdIncome)}</p><p className="text-xs text-stone-500">Total Income</p></CardContent></Card>
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-red-600">{fmt(data.ytdExpenses)}</p><p className="text-xs text-stone-500">Total Expenses</p></CardContent></Card>
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-[#1e3a5f]">{fmt(data.ytdNet)}</p><p className="text-xs text-stone-500">Net</p></CardContent></Card>
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-stone-700">{data.txnCount}</p><p className="text-xs text-stone-500">Transactions</p></CardContent></Card>
    </div>

    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Recent Transactions</CardTitle>
          <Badge variant="outline">All ({data.txnCount})</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {data.transactions.map((txn, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 px-2 rounded-lg hover:bg-stone-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${txn.type === 'income' ? 'bg-[#1e3a5f]/10 text-[#1e3a5f]' : 'bg-red-50 text-red-600'}`}>
                  {txn.type === 'income' ? '+' : '-'}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-stone-900 truncate">{txn.desc}</p>
                    {txn.recurring && <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-blue-600">monthly</Badge>}
                  </div>
                  <p className="text-[11px] text-stone-400">{txn.date} · {txn.cat}</p>
                </div>
              </div>
              <span className={`font-semibold text-sm ${txn.type === 'income' ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                {txn.type === 'income' ? '+' : ''}{fmtFull(txn.amount)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

const DemoBanking = ({ data }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-xl font-bold text-[#1e3a5f]">{fmtFull(data.totalBalance)}</p><p className="text-xs text-stone-500">Total Balance</p></CardContent></Card>
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-xl font-bold text-[#1e3a5f]">5</p><p className="text-xs text-stone-500">Accounts</p></CardContent></Card>
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-xl font-bold text-stone-400">0</p><p className="text-xs text-stone-500">Connected (Plaid)</p></CardContent></Card>
      <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-xl font-bold text-[#1e3a5f]">{data.txnCount}</p><p className="text-xs text-stone-500">Transactions</p></CardContent></Card>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {data.smartAccounts.map((acc) => (
        <Card key={acc.mask}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${acc.color}15` }}>
                  <svg className="w-5 h-5" style={{ color: acc.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/></svg>
                </div>
                <div>
                  <p className="font-medium text-sm text-stone-900">{acc.bank}</p>
                  <p className="text-[11px] text-stone-400">**** {acc.mask}</p>
                </div>
              </div>
              <Badge variant="outline" className="text-[10px]">{acc.percent > 0 ? `${acc.percent}%` : 'Primary'}</Badge>
            </div>
            <p className="text-xs text-stone-500">Current Balance</p>
            <p className="text-2xl font-bold text-[#1e3a5f]">{fmtFull(acc.balance)}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const PLAN_TIERS = [
  { key: "starter", name: "Starter", price: 50, annual: 500, description: "Perfect for new entrepreneurs", badge: null, features: ["1 business", "5 Opturna accounts", "Basic allocation tracking", "Email reminders", "Deductions library", "Basic reports"], highlighted: false },
  { key: "pro", name: "Pro", price: 99, annual: 990, description: "For growing businesses ready to scale", badge: "Most Popular", features: ["3 businesses", "5 Opturna accounts", "Bank connections (Plaid)", "PDF & CSV exports", "Tax planning tools", "Email & SMS reminders", "CPA email reports", "Financial health score", "Cash flow projections"], highlighted: true },
  { key: "enterprise", name: "Enterprise", price: 149, annual: 1490, description: "Full power for established businesses", badge: "Best Value", features: ["Unlimited businesses", "5 Opturna accounts", "Bank connections (Plaid)", "PDF & CSV exports", "Tax planning tools", "Email & SMS reminders", "CPA email reports", "Financial health score", "Cash flow projections", "Priority support", "Custom reports"], highlighted: false },
];

const DemoPlans = () => {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">Choose the Right Plan for Your Business</h2>
        <p className="text-sm text-stone-500">Start your 7-day free trial — no credit card required.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PLAN_TIERS.map((tier) => (
          <Card key={tier.key} className={`relative overflow-hidden transition-all duration-300 ${tier.highlighted ? 'ring-2 ring-[#1e3a5f] shadow-lg scale-[1.02]' : 'hover:shadow-md'}`} data-testid={`demo-plan-${tier.key}`}>
            {tier.badge && <div className="absolute top-0 right-0"><Badge className="bg-[#1e3a5f] text-white rounded-none rounded-bl-lg text-[10px] px-3 py-1">{tier.badge}</Badge></div>}
            <CardContent className="pt-6 pb-5">
              <h3 className="text-lg font-bold text-stone-900">{tier.name}</h3>
              <p className="text-xs text-stone-500 mb-4">{tier.description}</p>
              <div className="flex items-baseline gap-1 mb-1"><span className="text-3xl font-bold text-[#1e3a5f]">${tier.price}</span><span className="text-sm text-stone-400">/mo</span></div>
              <p className="text-[11px] text-stone-400 mb-5">or ${tier.annual}/yr (save ${tier.price * 12 - tier.annual})</p>
              <Button className={`w-full rounded-full mb-5 ${tier.highlighted ? 'bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`} onClick={() => navigate("/auth")} data-testid={`demo-plan-cta-${tier.key}`}>Start Free Trial</Button>
              <div className="space-y-2">
                {tier.features.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2 rounded-lg">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 bg-[#1e3a5f]"><svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg></div>
                    <span className="text-xs text-stone-600">{f}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8e] border-0">
        <CardContent className="py-6 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Ready to take control of your business finances?</h3>
          <p className="text-sm text-white/70 mb-4">Join thousands of business owners who save an average of $4,200/year in missed deductions.</p>
          <Button className="bg-white text-[#1e3a5f] hover:bg-white/90 rounded-full px-8 py-5 text-base font-semibold shadow-lg" onClick={() => navigate("/auth")} data-testid="demo-plan-bottom-cta">Start Your 7-Day Free Trial</Button>
        </CardContent>
      </Card>
    </div>
  );
};

const TAB_CONTENT = { dashboard: DemoDashboard, accounts: DemoAccounts, transactions: DemoTransactions, banking: DemoBanking };

// --- ONBOARDING FORM ---
const DemoOnboarding = ({ onComplete }) => {
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [revenue, setRevenue] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const rev = parseFloat(revenue);
    if (!businessName.trim() || !industry || !rev || rev <= 0) return;
    onComplete(businessName.trim(), industry, rev);
  };

  const isValid = businessName.trim() && industry && revenue && parseFloat(revenue) > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f2439] via-[#1e3a5f] to-[#2d5a8e] flex items-center justify-center p-4" data-testid="demo-onboarding">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <img src="/logo_compact.png" alt="Opturna Tax AI" className="h-10 mx-auto mb-6 rounded" />
          <h1 className="text-3xl font-bold text-white mb-3">See Your Numbers in Action</h1>
          <p className="text-white/70 text-base">Enter your business details below and we'll show you exactly how Opturna manages your money.</p>
        </div>

        <Card className="shadow-2xl border-0">
          <CardContent className="pt-8 pb-8 px-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="demo-biz-name" className="text-stone-700 font-medium">Business Name</Label>
                <Input
                  id="demo-biz-name"
                  placeholder="e.g., Johnson Consulting LLC"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="h-12 text-base"
                  data-testid="demo-business-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700 font-medium">Industry</Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger className="h-12 text-base" data-testid="demo-industry-select">
                    <SelectValue placeholder="Select your industry..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(ind => (
                      <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="demo-revenue" className="text-stone-700 font-medium">Average Monthly Revenue</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-base">$</span>
                  <Input
                    id="demo-revenue"
                    type="number"
                    min="1000"
                    step="100"
                    placeholder="15000"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    className="h-12 pl-8 text-base"
                    data-testid="demo-revenue-input"
                  />
                </div>
                <p className="text-xs text-stone-400">Enter your typical monthly revenue (before expenses)</p>
              </div>

              <Button
                type="submit"
                disabled={!isValid}
                className="w-full h-12 text-base font-semibold bg-[#1e3a5f] hover:bg-[#2d5a8e] rounded-full mt-2"
                data-testid="demo-start-btn"
              >
                Show Me My Dashboard
              </Button>

              <p className="text-center text-xs text-stone-400">No signup required. Your data stays in your browser.</p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// --- EMAIL CAPTURE ---
const EmailCapture = ({ data, onDismiss }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    // Capture UTM params from URL
    const params = new URLSearchParams(window.location.search);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      fetch(`${API}/demo/capture-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          business_name: data.businessName,
          industry: data.industry.value,
          monthly_revenue: data.monthlyRevenue,
          source: "interactive_demo",
          utm_source: params.get("utm_source"),
          utm_medium: params.get("utm_medium"),
          utm_campaign: params.get("utm_campaign"),
          utm_content: params.get("utm_content"),
          referrer: document.referrer || null,
        }),
      }).catch(() => {});
    } catch (err) {
      console.error("Lead capture failed:", err);
    }
    // Fire Meta Pixel Lead event
    if (window.fbq) window.fbq('track', 'Lead', { content_name: data.businessName, content_category: data.industry?.value });
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="email-capture-success">
        <Card className="max-w-md w-full shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-2">You're All Set!</h3>
            <p className="text-stone-600 mb-6">Start your free trial and see these numbers come to life with real data.</p>
            <Button className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e] rounded-full h-12 text-base" onClick={() => navigate("/auth")} data-testid="email-capture-signup">
              Start Free Trial
            </Button>
            <button className="text-sm text-stone-400 hover:text-stone-600 mt-3 block mx-auto" onClick={onDismiss}>Continue exploring</button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="email-capture-modal">
      <Card className="max-w-md w-full shadow-2xl">
        <CardContent className="pt-8 pb-8 px-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-stone-900 mb-2">Save Your Personalized Plan</h3>
            <p className="text-stone-600 text-sm">Enter your email and we'll send you a summary of your {data.businessName} financial projections.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              placeholder="you@yourbusiness.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 text-base"
              required
              data-testid="email-capture-input"
            />
            <Button type="submit" className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e] rounded-full h-12 text-base" data-testid="email-capture-submit">
              Save & Start Free Trial
            </Button>
          </form>
          <button className="text-sm text-stone-400 hover:text-stone-600 mt-4 block mx-auto" onClick={onDismiss} data-testid="email-capture-skip">
            Skip for now
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

// --- MAIN DEMO VIEW ---
const DemoView = () => {
  const navigate = useNavigate();
  const [demoData, setDemoData] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [guideStep, setGuideStep] = useState(0);
  const [showGuide, setShowGuide] = useState(true);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailCaptureShown, setEmailCaptureShown] = useState(false);

  // Drag state for guide
  const [guidePos, setGuidePos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const guideRef = useRef(null);

  const onPointerDown = useCallback((e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    const rect = guideRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    const maxX = window.innerWidth - (guideRef.current?.offsetWidth || 400);
    const maxY = window.innerHeight - (guideRef.current?.offsetHeight || 200);
    setGuidePos({ x: Math.max(0, Math.min(newX, maxX)), y: Math.max(0, Math.min(newY, maxY)) });
  }, [isDragging]);

  const onPointerUp = useCallback(() => setIsDragging(false), []);

  const handleOnboardingComplete = useCallback((name, industry, revenue) => {
    const data = generateDemoData(name, industry, revenue);
    setDemoData(data);
  }, []);

  const currentGuide = GUIDE_STEPS[guideStep];

  const nextGuide = () => {
    if (guideStep < GUIDE_STEPS.length - 1) {
      const next = guideStep + 1;
      setGuideStep(next);
      setActiveTab(GUIDE_STEPS[next].tab);
    } else {
      setShowGuide(false);
      // Show email capture when guide finishes
      if (!emailCaptureShown) {
        setShowEmailCapture(true);
        setEmailCaptureShown(true);
      }
    }
  };

  const prevGuide = () => {
    if (guideStep > 0) {
      const prev = guideStep - 1;
      setGuideStep(prev);
      setActiveTab(GUIDE_STEPS[prev].tab);
    }
  };

  const renderContent = () => {
    if (activeTab === "plans") return <DemoPlans />;
    const Component = TAB_CONTENT[activeTab];
    return Component ? <Component data={demoData} /> : null;
  };

  // Show onboarding form first
  if (!demoData) return <DemoOnboarding onComplete={handleOnboardingComplete} />;

  // Show email capture when switching to plans tab for first time
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === "plans" && !emailCaptureShown) {
      setShowEmailCapture(true);
      setEmailCaptureShown(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="demo-view" data-protected>
      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8e] text-white py-2.5 px-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-sm font-medium">Interactive Demo — Personalized for <strong>{demoData.businessName}</strong></span>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 text-xs" onClick={() => navigate("/")} data-testid="demo-exit-btn">Exit Demo</Button>
          <Button size="sm" className="bg-white text-[#1e3a5f] hover:bg-white/90 text-xs font-semibold rounded-full px-5" onClick={() => navigate("/auth")} data-testid="demo-signup-btn">Start Free Trial</Button>
        </div>
      </div>

      <div className="flex">
        {/* Mini Sidebar */}
        <aside className="w-56 bg-white border-r border-[#e4e8ee] min-h-[calc(100vh-44px)] flex flex-col">
          <div className="h-14 flex items-center px-4 border-b border-[#e4e8ee]">
            <img src="/logo_compact.png" alt="Opturna Tax AI" className="h-7 rounded" />
          </div>
          <div className="px-3 py-3 border-b border-[#e4e8ee]">
            <div className="w-full px-2.5 py-1.5 rounded-md border border-[#e4e8ee] bg-white text-xs font-medium text-[#1e3a5f] truncate">
              {demoData.businessName}
            </div>
          </div>
          <nav className="flex-1 px-2 py-3">
            {DEMO_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabSwitch(tab.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] mb-0.5 transition-colors ${activeTab === tab.key ? 'bg-[#1e3a5f]/10 text-[#1e3a5f] font-semibold' : 'text-[#5a6577] hover:bg-stone-50'}`}
                data-testid={`demo-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
            <div className="mt-4 pt-4 border-t border-stone-100 space-y-0.5">
              {["Tax Calculator", "Deductions Library", "AI Assistant", "Settings"].map(label => (
                <div key={label} className="px-3 py-2 rounded-lg text-[13px] text-stone-300 cursor-not-allowed">{label}</div>
              ))}
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-44px)]">
          <header className="h-14 bg-white border-b border-[#e4e8ee] flex items-center justify-between px-6">
            <h2 className="text-sm font-semibold text-[#1e3a5f]">{DEMO_TABS.find(t => t.key === activeTab)?.label}</h2>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-[#1e3a5f]">{demoData.businessName}</p>
                <p className="text-[11px] text-[#8b95a5]">{demoData.industry.label}</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center">
                <span className="text-white font-semibold text-xs">{demoData.businessName.charAt(0)}</span>
              </div>
            </div>
          </header>
          <div className="p-6 max-w-6xl">{renderContent()}</div>
        </main>
      </div>

      {/* Guide Overlay */}
      {showGuide && (
        <div
          ref={guideRef}
          data-testid="demo-guide"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          style={guidePos.x || guidePos.y
            ? { position: 'fixed', left: guidePos.x, top: guidePos.y, zIndex: 50 }
            : { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 50 }
          }
          className="w-full max-w-lg px-4 select-none"
        >
          <div className={`bg-white rounded-2xl shadow-2xl border border-stone-200 p-5 relative ${isDragging ? 'ring-2 ring-[#1e3a5f]/30' : ''}`} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
            <button onClick={() => setShowGuide(false)} className="absolute top-3 right-3 text-stone-400 hover:text-stone-600 transition-colors" data-testid="demo-guide-close">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-[#1e3a5f] flex items-center justify-center"><span className="text-white text-xs font-bold">{guideStep + 1}</span></div>
              <p className="text-xs text-stone-400">Step {guideStep + 1} of {GUIDE_STEPS.length}</p>
              <span className="ml-auto text-[10px] text-stone-300 italic">drag to move</span>
            </div>
            <h4 className="font-semibold text-stone-900 mb-1">{currentGuide.title}</h4>
            <p className="text-sm text-stone-600 mb-4">{currentGuide.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex gap-1.5">{GUIDE_STEPS.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === guideStep ? 'bg-[#1e3a5f]' : 'bg-stone-200'}`} />)}</div>
              <div className="flex gap-2">
                {guideStep > 0 && <Button variant="outline" size="sm" onClick={prevGuide} className="text-xs" data-testid="demo-guide-prev">Back</Button>}
                <Button size="sm" onClick={nextGuide} className="bg-[#1e3a5f] hover:bg-[#2d5a8e] text-xs" data-testid="demo-guide-next">
                  {guideStep < GUIDE_STEPS.length - 1 ? "Next" : "Got it!"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Capture Modal */}
      {showEmailCapture && <EmailCapture data={demoData} onDismiss={() => setShowEmailCapture(false)} />}
    </div>
  );
};

export default DemoView;
